<script>
  import { onMount } from 'svelte';
  import { Link, navigate } from 'svelte-routing';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import { login } from '../authService'; // Import login function
  import { authFetch } from '../authService'; 

  let items = [];
  let isEnabled = true;
  let isLoading = true;
  let error = '';
  let currentUser = null;

  authStore.subscribe(v => currentUser = v.user);

  onMount(async () => {
    // Check status
    try {
        const statusRes = await fetch(getApiUrl('/marketplace/status'));
        const status = await statusRes.json();
        isEnabled = status.enabled;

        if(isEnabled) {
            // Fetch items regardless of login status
            fetchItems();
        } else {
            isLoading = false;
        }
    } catch (e) {
        error = e.message;
        isLoading = false;
    }
  });

  async function fetchItems() {
      try {
          const res = await fetch(getApiUrl('/marketplace/items'));
          items = await res.json();
      } catch(e) { error = e.message; }
      finally { isLoading = false; }
  }

  // --- NEW: Protected Action Handlers ---
  
  function handleSell() {
      if (!currentUser) {
          // If not logged in, trigger login. 
          // MSAL will redirect back to this page after success.
          login();
      } else {
          // If logged in, go to the protected route
          navigate('/marketplace/sell');
      }
  }

  function handleBuyCredits() {
      if (!currentUser) {
          login();
      } else {
          navigate('/credits/topup');
      }
  }

  async function buyItem(item) {
      if (!currentUser) {
          login();
          return;
      }

      if(!confirm(`Buy "${item.title}" for ${item.price} credits?`)) return;
      
      try {
          const res = await authFetch(getApiUrl('/marketplace/buy'), {
              method: 'POST',
              headers: {'Content-Type': 'application/json'},
              body: JSON.stringify({ itemId: item._id, buyerId: currentUser.idTokenClaims.oid })
          });
          
          const result = await res.json();
          if (!res.ok) throw new Error(result.error);
          
          alert("Purchase Successful! Check 'My Inbox' or details for delivery/codes.");
          fetchItems(); // Refresh
      } catch(e) {
          alert(e.message);
      }
  }
</script>

<div class="market-container">
    <div class="header">
        <h2>Marketplace</h2>
        {#if isEnabled}
            <div class="header-actions">
                <!-- Use buttons with handlers instead of direct Links -->
                <button class="sell-btn" on:click={handleSell}>+ Sell Item</button>
                <button class="topup-btn" on:click={handleBuyCredits}>Buy Credits</button>
            </div>
        {/if}
    </div>

    {#if !isEnabled}
        <div class="disabled-msg">The Marketplace is currently unavailable.</div>
    {:else if isLoading}
        <p>Loading items...</p>
    {:else if items.length === 0}
        <p>No items for sale right now.</p>
    {:else}
        <div class="grid">
            {#each items as item}
                <div class="card">
                    {#if item.images && item.images.length > 0}
                        <img src={item.images[0]} alt={item.title} class="thumb" />
                    {/if}
                    <div class="content">
                        <h3>{item.title}</h3>
                        <p class="desc">{item.description}</p>
                        <div class="meta">
                            <span class="price">{item.price === 0 ? 'FREE' : `${item.price} Credits`}</span>
                            <span class="delivery">{item.type} / {item.delivery}</span>
                        </div>
                        <div class="footer">
                            <small>By: {item.sellerName}</small>
                            <!-- Hide Buy button if user is the seller -->
                            {#if !currentUser || currentUser.idTokenClaims.oid !== item.sellerId}
                                <button class="buy-btn" on:click={() => buyItem(item)}>Buy Now</button>
                            {/if}
                        </div>
                    </div>
                </div>
            {/each}
        </div>
    {/if}
</div>

<style>
    .market-container { max-width: 1000px; margin: 2rem auto; padding: 1rem; }
    .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; flex-wrap: wrap; gap: 1rem;}
    
    .sell-btn { background: #28a745; color: white; padding: 0.7rem 1.2rem; border-radius: 4px; border: none; font-size: 1rem; cursor: pointer; }
    .topup-btn { background: #ffc107; color: #333; padding: 0.7rem 1.2rem; border-radius: 4px; border: none; font-size: 1rem; cursor: pointer; margin-left: 1rem; }
    .buy-btn { background: #007bff; color: white; border: none; padding: 0.5rem 1rem; border-radius: 4px; cursor: pointer; }
    
    .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1.5rem; }
    .card { background: white; border: 1px solid #eee; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 5px rgba(0,0,0,0.05); }
    .thumb { width: 100%; height: 180px; object-fit: cover; }
    .content { padding: 1rem; }
    h3 { margin: 0 0 0.5rem 0; }
    .desc { color: #666; font-size: 0.9rem; margin-bottom: 1rem; }
    .meta { display: flex; justify-content: space-between; font-weight: bold; margin-bottom: 1rem; }
    .price { color: #007bff; }
    .footer { display: flex; justify-content: space-between; align-items: center; border-top: 1px solid #eee; padding-top: 0.8rem; }
    .disabled-msg { text-align: center; padding: 3rem; background: #eee; border-radius: 8px; }
</style>